import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../../Navbar/Navbar";
import Footer from "../../../Footer/Footer";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Box,
  Paper,
  IconButton,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";
import styles from "./EditListing.module.css";
import {
  normalizeUnit,
  generateAddressSignature,
} from "../../../../util/utilFunctions";
import PremiumListing from "./PremiumListing";
import { DateTime } from "luxon";
import ImageUploading from "react-images-uploading";

const EditListing = () => {
  const { accessToken } = useAuth();
  const { url } = useParams();
  const navigate = useNavigate();

  const [listingData, setListingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [trialOver, setTrialOver] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [timelines, setTimelines] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);

  const formatZonedTime = (timestamp, zone) => {
    const fallbackZone = zone || "UTC";
    const dt = DateTime.fromISO(timestamp, { zone: fallbackZone });

    return dt.isValid ? dt.toFormat("ff") : "Invalid Date";
  };

  const toLocalInputFormat = (isoString, zone) => {
    return DateTime.fromISO(isoString, { zone: zone || "UTC" }).toFormat(
      "yyyy-MM-dd'T'HH:mm"
    );
  };

  const [propImages, setPropImages] = useState();
  const maxNumber = 12;

  const onPropImagesChange = (imageList, addUpdateIndex) => {
    setPropImages(imageList);
  };

  // Fetch listing
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/listings/${url}`
        );
        if (response.data) {
          setListingData(response.data.listing);
          setSelectedAgency(response.data.listing.agency_id || -1);
        }
      } catch (error) {
        console.error("Error fetching listing:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [url]);

  useEffect(() => {
    if (listingData?.propertyImages?.length > 0) {
      const initialImages = listingData.propertyImages.map((img) => ({
        data_url: img.image_url,
      }));
      setPropImages(initialImages);
    }
  }, [listingData]);

  // Trial expiry logic
  useEffect(() => {
    if (listingData?.premium_started_at) {
      const startedDate = new Date(listingData.premium_started_at);
      const trialEnd = new Date(
        startedDate.getTime() + 1 * 24 * 60 * 60 * 1000
      );
      const now = new Date();
      setTrialOver(listingData.property_is_trial && now >= trialEnd);
    }
  }, [listingData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setListingData({ ...listingData, [name]: value });
  };

  const uploadImages = async (propertyId, accessToken) => {
    try {
      const formData = new FormData();

      const newImages = [];
      const existingImageUrls = [];

      if (propImages && Array.isArray(propImages)) {
        propImages.forEach((image) => {
          if (image.file) {
            formData.append("images", image.file); // new file
          } else if (image.data_url && image.data_url.startsWith("https://")) {
            existingImageUrls.push(image.data_url); // existing image URL
          }
        });
      }

      formData.append("propertyId", propertyId);
      formData.append("existingImageUrls", JSON.stringify(existingImageUrls));

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/properties/upload-images`,
        formData,
        {
          headers: {
            Authorization: accessToken,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.success) {
        return true;
      } else {
        const errorMsg =
          response.data?.message ||
          "Some error occurred while uploading images!";
        showErrorMessage(errorMsg);
        return false;
      }
    } catch (error) {
      console.error("Error during image upload:", error);
      showErrorMessage(
        "Some error occurred while uploading images. Please try again later"
      );
      return false;
    }
  };

  const showErrorMessage = (text) => {
    // toast("Check!");
    toast.error(text, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      // draggable: true,
      progress: undefined,
      theme: "colored",
      // transition: Bounce,
    });
  };

  const handleSave = async () => {
    listingData.address = `${listingData.street} ${listingData.street_2} ${listingData.city} ${listingData.state} ${listingData.zipcode}`;
    const payload = {
      ...listingData,
      agency_id: selectedAgency !== "-1" ? selectedAgency : null,
    };

    try {
      await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/listings/update/${listingData.id}`,
        payload,
        { headers: { Authorization: accessToken } }
      );

      if (propImages) {
        var isUploadSuccess = await uploadImages(
          listingData.property_id,
          accessToken
        );

        if (!isUploadSuccess) {
          // @TODO: notify admin
          console.error("Unable to upload images!");
        }
      }
      if (listingData.pro_id) {
        navigate("/MyFirstLand?category=prolistings");
      } else {
        navigate("/MyFirstLand?category=listings");
      }
    } catch (error) {
      toast.error(
        `Error updating: ${error?.response?.data?.message || error.message}`
      );
    }
  };

  function getNowLocalDatetime() {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");

    const YYYY = now.getFullYear();
    const MM = pad(now.getMonth() + 1);
    const DD = pad(now.getDate());
    const hh = pad(now.getHours());
    const mm = pad(now.getMinutes());

    return `${YYYY}-${MM}-${DD}T${hh}:${mm}`;
  }
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const [openDlg, setOpenDlg] = useState(false);

  const [openPropertyDialog, setOpenPropertyDialog] = useState(false);
  const [openStartDate, setOpenStartDate] = useState(getNowLocalDatetime());
  const [openEndDate, setOpenEndDate] = useState(
    listingData?.open_property_end_date || ""
  );
  const [comments, setComments] = useState(listingData?.comments || "");

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(-1);

  // Handlers to open/close
  const handleOpenPropertyDialog = () => setOpenPropertyDialog(true);
  const handleClosePropertyDialog = () => {
    setOpenPropertyDialog(false);
    // reset if you like:
    setOpenStartDate(listingData.open_property_start_date || "");
    setOpenEndDate(listingData.open_property_end_date || "");
    setComments(listingData.comments || "");
  };

  const handleAddOpenProperty = () => {
    // merge into your listingData and then close:
    setListingData({
      ...listingData,
      open_property: true,
      open_property_start_date: openStartDate,
      open_property_end_date: openEndDate,
      comments: comments,
    });
    setOpenPropertyDialog(false);
  };

  function toDateTimeLocal(isoString) {
    if (!isoString) return "";
    // create a Date, correct for local timezone, then strip seconds
    const dt = new Date(isoString);
    const tzOffsetMs = dt.getTimezoneOffset() * 60000;
    const localISO = new Date(dt.getTime() - tzOffsetMs)
      .toISOString()
      .slice(0, 16); // "YYYY-MM-DDTHH:mm"
    return localISO;
  }

  useEffect(() => {
    const fetchAgencies = async (accessToken) => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/list`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.status === 200 && response.data && response.data.success) {
          setAgencies(response.data.agencies || []);
        }
      } catch (error) {
        // console.log(error);
      } finally {
      }
    };

    if (accessToken) {
      fetchAgencies(accessToken);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!listingData?.id) return;
    axios
      .get(
        `${import.meta.env.VITE_BACKEND_URL}/listings/open-property/${
          listingData.id
        }`,
        { headers: { Authorization: accessToken } }
      )
      .then((res) => {
        if (res.data.success) {
          setTimelines(res.data.data);
        } else {
          setTimelines([]); // fallback
        }
      })
      .catch((err) => {
        console.error("Error fetching timelines:", err);
        toast.error("Could not load open‑property timeframes.");
        setTimelines([]);
      });
  }, [listingData?.id, accessToken]);

  // open dialog to add (index = null) or edit (index >= 0)
  const openTimelineDialog = (index = null) => {
    setEditingIndex(index);
    if (index !== null) {
      const tl = timelines[index];
      setOpenStartDate(toLocalInputFormat(tl.open_from, tl.timezone));
      setOpenEndDate(toLocalInputFormat(tl.open_to, tl.timezone));
      setComments(tl.comments);
    } else {
      // clear fields for a new entry
      setOpenStartDate(getNowLocalDatetime());
      setOpenEndDate("");
      setComments("");
    }
    setOpenPropertyDialog(true);
  };

  const getNowLocal = () => new Date().toISOString().slice(0, 16);

  const handleSaveTimeline = async () => {
    if (!listingData?.id) return;
    const payload = {
      open_from: openStartDate,
      open_to: openEndDate,
      comments,
      timezone: userTimeZone,
    };

    try {
      let saved;
      if (editingIndex !== null) {
        // EDIT
        const id = timelines[editingIndex].id;
        const res = await axios.put(
          `${import.meta.env.VITE_BACKEND_URL}/listings/open-property/${
            listingData.id
          }/${id}`,
          payload,
          { headers: { Authorization: accessToken } }
        );
        saved = res.data.data ?? res.data;
        setTimelines((ts) =>
          ts.map((t, i) => (i === editingIndex ? saved : t))
        );

        toast.success("Open‑house slot updated successfully!");
      } else {
        // CREATE
        const res = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/listings/open-property/${
            listingData.id
          }`,
          payload,
          { headers: { Authorization: accessToken } }
        );
        saved = res.data.data ?? res.data;
        setTimelines((ts) => [...ts, saved]);

        toast.success("Open‑house slot added successfully!");
      }

      setOpenPropertyDialog(false);
      setEditingIndex(null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save timeframe.");
    }
  };

  const handleDeleteTimeline = async (index) => {
    if (!listingData?.id) return;
    const id = timelines[index]?.id;
    if (!id) {
      return;
    }
    try {
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/listings/open-property/${
          listingData.id
        }/${id}`,
        { headers: { Authorization: accessToken } }
      );
      setTimelines((ts) => ts.filter((_, i) => i !== index));

      toast.success("Open‑house slot deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to delete timeframe.");
    }
  };

  const handleDelete = async () => {
    if (!deleteReason) return;
    try {
      const res = await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/listings/delete/${listingData.id}`,
        {
          headers: { Authorization: accessToken },
          data: { reason: deleteReason },
        }
      );
      if (res.status === 200 && res.data.success) {
        await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/listing/delete/email/send`,
          {
            listingId: listingData.id,
            deleteReason: deleteReason,
          },
          { headers: { Authorization: accessToken } }
        );
        toast.success("Listing deleted and email sent!");
        navigate("/MyFirstLand?category=listings");
      }
    } catch (error) {
      toast.error(
        `Error deleting: ${error?.response?.data?.message || error.message}`
      );
    }
  };

  const handleCancelPremium = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/payment/cancel-premium`,
        { listingId: listingData.id },
        { headers: { Authorization: accessToken } }
      );
      if (response.data.success) {
        toast.success(response.data.success);
        navigate(0); // Refresh the page to reflect changes
      } else {
        toast.error("Cancellation failed.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error during cancellation.");
    }
    setCancelDialogOpen(false);
  };

  const handleExtendPremium = async () => {
    try {
      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/payment/create-premium-checkout-session`,
        { listingId: listingData.id, isExtension: true },
        { headers: { Authorization: accessToken } }
      );
      if (response.data.success && response.data.paymentUrl) {
        toast.success("Redirecting to Stripe checkout...");
        // Redirect user to Stripe checkout page
        window.location.href = response.data.paymentUrl;
      } else {
        toast.error("Failed to start premium extension payment.");
      }
    } catch (error) {
      toast.error("Error extending premium.");
    }
  };

  const isStartPast = DateTime.fromISO(openStartDate) < DateTime.local();
  const isEndBeforeStart =
    DateTime.fromISO(openEndDate) < DateTime.fromISO(openStartDate);

  const handleReactivatePremium = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/payment/reactivate-premium`,
        { listingId: listingData.id },
        { headers: { Authorization: accessToken } }
      );
      if (response.data.success) {
        toast.success("Premium reactivated.");
        navigate(0);
      }
    } catch (error) {
      toast.error("Error reactivating premium.");
    }
  };

  const updatePremiumOptIn = () => {
    navigate("/Landlord/Payment", {
      state: { listingId: listingData.id || null },
    });
  };

  const nowLocal = new Date().toISOString().slice(0, 16);

  const now = new Date().toISOString().slice(0, 16);

  if (loading || !listingData) return <Typography>Loading...</Typography>;

  return (
    <>
      <Navbar />
      <Box p={4}>
        <Paper
          elevation={3}
          sx={{ padding: 4, maxWidth: "800px", margin: "auto" }}
        >
          <Typography variant="h4" gutterBottom>
            Edit Listing
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                name="title"
                value={listingData.title || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bedrooms"
                name="bedrooms"
                type="number"
                value={listingData.bedrooms || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bathrooms"
                name="bathrooms"
                type="number"
                value={listingData.bathrooms || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Area (sqft)"
                name="area_sqft"
                type="number"
                value={listingData.area_sqft || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Street Address"
                name="street"
                value={listingData.street || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Unit/Apt"
                name="street_2"
                value={listingData.street_2 || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                name="city"
                value={listingData.city || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="State"
                name="state"
                value={listingData.state || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Zipcode"
                name="zipcode"
                value={listingData.zipcode || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Heating/Cooling"
                name="heating_cooling"
                value={listingData.heating_cooling || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Landmark"
                name="landmark"
                value={listingData.landmark || ""}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={listingData.description || ""}
                onChange={handleInputChange}
                multiline
                rows={4}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="agency-select"
                select
                label="Edit Agency or As a freelancer"
                value={selectedAgency ?? ""}
                onChange={(e) => {
                  // console.log(e.target.value);
                  setSelectedAgency(e.target.value);
                }}
                variant="outlined"
                fullWidth
              >
                {/* Option for "Personal" */}
                <MenuItem value="-1">Freelancer</MenuItem>

                {/* Dynamically render agency options */}
                {agencies.map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>
                    {agency.agency_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {listingData.pro_id && (
            <Box mt={4}>
              <Typography variant="h6">Open Property Timeframes</Typography>

              {timelines.length === 0 && (
                <Typography color="textSecondary">
                  No timeframes yet.
                </Typography>
              )}

              {timelines.map((tl, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={1}
                  mt={1}
                  bgcolor="#f1f1f1"
                  borderRadius={1}
                >
                  <Box
                    component="div"
                    sx={{
                      display: "inline-flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1, // spacing between items
                    }}
                  >
                    <Typography>
                      {formatZonedTime(tl.open_from, tl.timezone)} ↔{" "}
                      {formatZonedTime(tl.open_to, tl.timezone)}
                    </Typography>

                    {tl.comments && (
                      <Typography component="span" variant="caption">
                        — {tl.comments}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openTimelineDialog(idx)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTimeline(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Button
                sx={{ mt: 2 }}
                variant="outlined"
                onClick={() => openTimelineDialog(idx)}
              >
                Add Open Property Timeframe
              </Button>
            </Box>
          )}

          <div className={styles.formComponent}>
            <ImageUploading
              multiple
              value={propImages ?? ""}
              onChange={onPropImagesChange}
              onError={(errorList, files) => {
                console.error("Image upload error:", errorList, files);

                if (errorList.maxNumber) {
                  toast.error(`You can only upload up to ${maxNumber} images.`);
                }
                if (errorList.acceptType) {
                  toast.error(
                    "Some files have unsupported file types. Please use JPG, PNG, JPEG, or WEBP."
                  );
                }
                if (errorList.maxFileSize) {
                  toast.error(
                    "Some files are too large. Maximum file size is 5MB."
                  );
                }
                if (errorList.resolution) {
                  toast.error("Some files have invalid resolution.");
                }
              }}
              acceptType={["jpg", "png", "jpeg", "webp"]}
              maxFileSize={1024 * 1024 * 5} // 5MB
              maxNumber={maxNumber}
              dataURLKey="data_url"
            >
              {({
                imageList,
                onImageUpload,
                onImageRemoveAll,
                onImageUpdate,
                onImageRemove,
                isDragging,
                dragProps,
                errors,
              }) => (
                <>
                  <div className={styles.uploadImageWrapper}>
                    <div
                      {...dragProps}
                      onClick={onImageUpload}
                      className={styles.uploadImageBox}
                    >
                      {isDragging ? (
                        <div className={styles.uploadImagePlace}>
                          Place here
                        </div>
                      ) : (
                        <div className={styles.uploadImageDrag}>
                          <div className={styles.uploadImageDragText}>
                            Drag and drop images here to upload!
                          </div>
                          <div className={styles.uploadImageOrText}>Or</div>
                          <div className={styles.uploadImageSelectBtn}>
                            Select images to upload
                          </div>
                        </div>
                      )}
                    </div>
                    &nbsp;
                    {imageList && imageList.length > 0 && (
                      <div className={styles.loadedImageContainer}>
                        <div className={styles.loadedImageContainerHeader}>
                          <div>Uploaded Images</div>
                          <div>
                            <button onClick={onImageRemoveAll}>
                              Remove all images
                            </button>
                          </div>
                        </div>
                        <div className={styles.loadedImageContainerBody}>
                          {imageList.map((image, index) => (
                            <div key={index} className={styles.imageItem}>
                              <img
                                src={image["data_url"]}
                                alt={`Uploaded-${index}`}
                                width="100"
                              />
                              <div className={styles.imageItemBtnWrapper}>
                                <button onClick={() => onImageUpdate(index)}>
                                  Update
                                </button>
                                <button onClick={() => onImageRemove(index)}>
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </ImageUploading>
          </div>

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save Changes
            </Button>

            <Button variant="outlined" color="error" onClick={handleOpenDialog}>
              Delete Listing
            </Button>
          </Box>
        </Paper>

        {/* PREMIUM SERVICE MANAGER */}
        <PremiumListing
          listingData={listingData}
          trialOver={trialOver}
          accessToken={accessToken}
          navigate={navigate}
          cancelDialogOpen={cancelDialogOpen}
          setCancelDialogOpen={setCancelDialogOpen}
          handleCancelPremium={handleCancelPremium}
          handleExtendPremium={handleExtendPremium}
          handleReactivatePremium={handleReactivatePremium}
          updatePremiumOptIn={updatePremiumOptIn}
        />

        {/* DELETE DIALOG */}
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this listing? This action cannot
              be undone.
            </DialogContentText>
            <div style={{ marginTop: "16px" }}>
              <label htmlFor="delete-reason">Reason for deleting:</label>
              <select
                id="delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="">Select a reason</option>
                <option value="sold">Property is sold</option>
                <option value="unavailable">Property is unavailable</option>
                <option value="other">Other</option>
              </select>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              No
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              autoFocus
              disabled={!deleteReason}
            >
              Yes, Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openPropertyDialog} onClose={handleClosePropertyDialog}>
          <DialogTitle>Add Open Property Timeframe</DialogTitle>

          <DialogContent dividers>
            <DialogContentText>
              Specify the timeframe and a brief description for this Open
              Property listing.
            </DialogContentText>

            <Grid container spacing={4} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date & Time"
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={openStartDate}
                  onChange={(e) => setOpenStartDate(e.target.value)}
                  inputProps={{
                    min: getNowLocal(),
                  }}
                  helperText={
                    openStartDate < now ? "Start cannot be in the past" : ""
                  }
                  error={openStartDate < now}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date & Time"
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={openEndDate}
                  onChange={(e) => setOpenEndDate(e.target.value)}
                  inputProps={{
                    min: openStartDate || nowLocal,
                  }}
                  helperText={
                    openEndDate && openStartDate && openEndDate < openStartDate
                      ? "End must be after start"
                      : ""
                  }
                  error={
                    openEndDate && openStartDate && openEndDate < openStartDate
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Comments"
                  multiline
                  rows={3}
                  fullWidth
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClosePropertyDialog} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleSaveTimeline}
              variant="contained"
              disabled={
                // no dates chosen
                !openStartDate ||
                !openEndDate ||
                // start is before now
                isStartPast ||
                // end is before start
                isEndBeforeStart
              }
            >
              {editingIndex !== null ? "Save" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Footer />
    </>
  );
};

export default EditListing;
